import reference from './geozones-reference.json';
import district from './district-reference.json';
import provenance from './provenance.json';

const EARTH_RADIUS_METERS = 6371008.8;
const RAD = Math.PI / 180;
const EDGE_EPSILON = 1e-12;
const CACHE_LIMIT = 1024;
export const BOUNDARY_REVIEW_DISTANCE_METERS = 30;

function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
}

export const GEOZONES = deepFreeze(reference);
export const DISTRICT = deepFreeze(district);
export const GEOGRAPHY_PROVENANCE = deepFreeze(provenance);
export const ZONES = Object.freeze(GEOZONES.features.map(feature => Object.freeze({
  id: feature.id,
  nameEn: feature.properties.nameEn,
  nameAr: feature.properties.nameAr,
})));
const zoneLabels = new Map(ZONES.map(zone => [zone.id, zone]));

export function getZoneLabel(id, isRTL = false) {
  const zone = zoneLabels.get(id);
  if (!zone) return '';
  return isRTL ? (zone.nameAr || zone.nameEn) : zone.nameEn;
}

function polygons(geometry) {
  if (geometry?.type === 'Polygon') return [geometry.coordinates];
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates;
  throw new Error('Geozones require Polygon or MultiPolygon geometry');
}

function boundsFor(polys) {
  const bounds = { minLat: Infinity, maxLat: -Infinity, minLng: Infinity, maxLng: -Infinity };
  polys.forEach(poly => poly.forEach(ring => ring.forEach(([lng, lat]) => {
    bounds.minLat = Math.min(bounds.minLat, lat);
    bounds.maxLat = Math.max(bounds.maxLat, lat);
    bounds.minLng = Math.min(bounds.minLng, lng);
    bounds.maxLng = Math.max(bounds.maxLng, lng);
  })));
  return bounds;
}

export const DISTRICT_BOUNDS = Object.freeze(boundsFor(DISTRICT.features.flatMap(feature => polygons(feature.geometry))));

function validCoordinates(coords) {
  return typeof coords?.lat === 'number' && typeof coords?.lng === 'number' &&
    Number.isFinite(coords.lat) && Number.isFinite(coords.lng) &&
    Math.abs(coords.lat) <= 90 && Math.abs(coords.lng) <= 180;
}

function inBounds({ lat, lng }, bounds) {
  return lat >= bounds.minLat - EDGE_EPSILON && lat <= bounds.maxLat + EDGE_EPSILON &&
    lng >= bounds.minLng - EDGE_EPSILON && lng <= bounds.maxLng + EDGE_EPSILON;
}

// Return 0 outside, 1 inside, 2 on an edge. Ring direction does not affect membership.
function ringMembership({ lat, lng }, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ax, ay] = ring[j];
    const [bx, by] = ring[i];
    const dx = bx - ax;
    const dy = by - ay;
    const length = Math.hypot(dx, dy);
    if (length === 0) {
      if (Math.hypot(lng - ax, lat - ay) <= EDGE_EPSILON) return 2;
      continue;
    }
    const cross = (lng - ax) * dy - (lat - ay) * dx;
    if (Math.abs(cross) <= EDGE_EPSILON * length &&
        lng >= Math.min(ax, bx) - EDGE_EPSILON && lng <= Math.max(ax, bx) + EDGE_EPSILON &&
        lat >= Math.min(ay, by) - EDGE_EPSILON && lat <= Math.max(ay, by) + EDGE_EPSILON) return 2;
    if ((ay > lat) !== (by > lat) && lng < (bx - ax) * (lat - ay) / (by - ay) + ax) inside = !inside;
  }
  return inside ? 1 : 0;
}

function polygonCovers(coords, poly) {
  const outer = ringMembership(coords, poly[0]);
  if (!outer) return false;
  if (outer === 2) return true;
  for (const hole of poly.slice(1)) {
    const membership = ringMembership(coords, hole);
    if (membership === 2) return true; // A hole's edge belongs to the polygon boundary.
    if (membership === 1) return false;
  }
  return true;
}

function angularDistance(a, b) {
  const h = Math.sin((b.lat - a.lat) / 2) ** 2 +
    Math.cos(a.lat) * Math.cos(b.lat) * Math.sin((b.lng - a.lng) / 2) ** 2;
  return 2 * Math.asin(Math.sqrt(Math.max(0, Math.min(1, h))));
}

function bearing(a, b) {
  const delta = b.lng - a.lng;
  return Math.atan2(Math.sin(delta) * Math.cos(b.lat),
    Math.cos(a.lat) * Math.sin(b.lat) - Math.sin(a.lat) * Math.cos(b.lat) * Math.cos(delta));
}

function prepareSegments(polys) {
  return polys.flatMap(poly => poly.flatMap(ring => ring.map((point, index) => {
    const next = ring[(index + 1) % ring.length];
    const a = { lng: point[0] * RAD, lat: point[1] * RAD };
    const b = { lng: next[0] * RAD, lat: next[1] * RAD };
    return { a, b, length: angularDistance(a, b), bearing: bearing(a, b) };
  })));
}

// Spherical segment distances are an approximate review aid, not survey measurements.
function segmentDistance(point, segment) {
  const fromStart = angularDistance(segment.a, point);
  if (segment.length === 0) return fromStart * EARTH_RADIUS_METERS;
  const angle = bearing(segment.a, point) - segment.bearing;
  const along = Math.atan2(Math.sin(fromStart) * Math.cos(angle), Math.cos(fromStart));
  if (along < 0 || along > segment.length) {
    return Math.min(fromStart, angularDistance(segment.b, point)) * EARTH_RADIUS_METERS;
  }
  const cross = Math.asin(Math.max(-1, Math.min(1, Math.sin(fromStart) * Math.sin(angle))));
  return Math.abs(cross) * EARTH_RADIUS_METERS;
}

const missingResult = Object.freeze({ zoneIds: Object.freeze([]), status: 'missing', distanceToBoundaryMeters: null });

/** Compile a fixed local GeoJSON dataset. Inputs are copied and results frozen for safe caching. */
export function createGeozoneClassifier(featureCollection) {
  const features = featureCollection.features.map(feature => {
    const polys = polygons(JSON.parse(JSON.stringify(feature.geometry)));
    const bounds = boundsFor(polys);
    const segments = prepareSegments(polys);
    const center = { lat: (bounds.minLat + bounds.maxLat) * RAD / 2, lng: (bounds.minLng + bounds.maxLng) * RAD / 2 };
    const radius = Math.max(...segments.map(segment => angularDistance(center, segment.a)));
    return { id: feature.id || feature.properties.id, polys, bounds, segments, center, radius };
  });
  const cache = new Map();
  return coords => {
    if (!validCoordinates(coords)) return missingResult;
    const key = `${coords.lat},${coords.lng}`;
    if (cache.has(key)) return cache.get(key);
    const point = { lat: coords.lat * RAD, lng: coords.lng * RAD };
    const zoneIds = [];
    let minimumDistance = Infinity;
    features.forEach(feature => {
      if (inBounds(coords, feature.bounds) && feature.polys.some(poly => polygonCovers(coords, poly))) {
        zoneIds.push(feature.id);
      }
    });
    // A spherical cap smaller than a hemisphere also contains each shortest boundary arc.
    // Its lower bound lets distant zones skip expensive segment distances without changing results.
    const nearestFeatures = features.map(feature => ({
      feature,
      lowerBound: feature.radius < Math.PI / 2
        ? Math.max(0, angularDistance(point, feature.center) - feature.radius) * EARTH_RADIUS_METERS
        : 0,
    })).sort((a, b) => a.lowerBound - b.lowerBound);
    for (const { feature, lowerBound } of nearestFeatures) {
      if (lowerBound > minimumDistance) break;
      feature.segments.forEach(segment => {
        minimumDistance = Math.min(minimumDistance, segmentDistance(point, segment));
      });
    }
    const status = !zoneIds.length ? 'outside' : zoneIds.length > 1 ? 'ambiguous' :
      minimumDistance <= BOUNDARY_REVIEW_DISTANCE_METERS ? 'near_boundary' : 'inside';
    const result = Object.freeze({
      zoneIds: Object.freeze(zoneIds),
      status,
      distanceToBoundaryMeters: Number.isFinite(minimumDistance) ? minimumDistance : null,
    });
    if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
    cache.set(key, result);
    return result;
  };
}

export const classifyCoordinates = createGeozoneClassifier(GEOZONES);

export function isInDistrict(coords) {
  return classifyCoordinates(coords).zoneIds.length > 0;
}
